import { useCallback, useEffect, useRef, useState } from 'react';
import type { FocusEvent, ReactNode } from 'react';
import {
  CarouselLayout,
  ConnectionQualityIndicator,
  DisconnectButton,
  GridLayout,
  MediaDeviceMenu,
  ParticipantName,
  StartAudio,
  TrackRefContext,
  TrackToggle,
  VideoTrack,
  isTrackReference,
  useEnsureTrackRef,
  useIsMuted,
  useIsSpeaking,
  useTracks,
} from '@livekit/components-react';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { Track } from 'livekit-client';

/**
 * The call's own stage: tiles, a floating self-view and a control dock, in
 * place of LiveKit's prebuilt <VideoConference/>.
 *
 * WHY NOT THE PREBUILT. It lays everyone out as equals in a grid, yourself
 * included, with a toolbar bolted under it. That is a conference-room layout,
 * and a call between two people in it reads as two thumbnails. A personal call
 * should look like one: the other person fills the screen, you are a small
 * picture in the corner, and the controls sit on top of the video rather than
 * taking a strip of it.
 *
 * Everything underneath is still LiveKit — the track hooks, VideoTrack, the
 * toggles, the device menus and the grid's pagination — so subscription,
 * adaptive stream and device handling are theirs, not a second copy of ours.
 */

/** How long the dock stays up after the pointer stops moving over the video. */
const DOCK_IDLE_MS = 3500;

/** Corners the self-view can sit in; a click on it moves it to the next one. */
const CORNERS = ['br', 'bl', 'tl', 'tr'] as const;
type Corner = (typeof CORNERS)[number];

/** Per-tile overlay, for MN-11's stats readout. Rendered inside the tile. */
export type TileOverlay = (trackRef: TrackReferenceOrPlaceholder) => ReactNode;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * One person's picture, or their initials when there is no picture to show.
 *
 * Reads its track from context, so it works both as GridLayout's template and
 * standalone inside a TrackRefContext.Provider.
 */
function Tile({
  fit = 'cover',
  overlay,
  mirror,
}: {
  fit?: 'cover' | 'contain';
  overlay?: TileOverlay;
  mirror?: boolean;
}) {
  const ref = useEnsureTrackRef();
  const p = ref.participant;
  const speaking = useIsSpeaking(p);
  const micMuted = useIsMuted({ participant: p, source: Track.Source.Microphone });
  const camMuted = useIsMuted(ref);
  const isScreen = ref.source === Track.Source.ScreenShare;
  const showVideo = isTrackReference(ref) && (isScreen || !camMuted);

  return (
    <div
      className="mx-tile"
      data-speaking={speaking && !isScreen ? 'true' : undefined}
      data-fit={fit}
      data-mirror={mirror ? 'true' : undefined}
    >
      {showVideo ? (
        <VideoTrack trackRef={ref} className="mx-tile-video" />
      ) : (
        <div className="mx-tile-avatar" aria-hidden="true">
          <span>{initials(p.name || p.identity)}</span>
        </div>
      )}
      <div className="mx-tile-plate">
        {micMuted && !isScreen && (
          <svg className="mx-tile-mic" viewBox="0 0 16 16" aria-label="Microphone off">
            <path
              d="M8 1.5a2.5 2.5 0 0 0-2.5 2.5v4a2.5 2.5 0 0 0 4.3 1.7M10.5 6.5V4M3.5 7.5a4.5 4.5 0 0 0 7.6 3.3M12.5 7.5c0 .6-.1 1.1-.3 1.6M8 12v2.5M2 2l12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        )}
        <ParticipantName className="mx-tile-name" />
        {isScreen && <span className="mx-tile-tag">screen</span>}
        {!p.isLocal && <ConnectionQualityIndicator className="mx-tile-conn" />}
      </div>
      {overlay && <div className="mx-tile-overlay">{overlay(ref)}</div>}
    </div>
  );
}

/** A Tile for a specific track, outside of a layout that provides one. */
function TileFor({
  trackRef,
  ...rest
}: { trackRef: TrackReferenceOrPlaceholder } & Parameters<typeof Tile>[0]) {
  return (
    <TrackRefContext.Provider value={trackRef}>
      <Tile {...rest} />
    </TrackRefContext.Provider>
  );
}

/**
 * You, small, in a corner. Click to move it to the next corner — the one it
 * starts in is not always the one that is out of the way.
 */
function SelfView({ trackRef, overlay }: { trackRef: TrackReferenceOrPlaceholder; overlay?: TileOverlay }) {
  const [corner, setCorner] = useState<Corner>('br');
  const next = () => setCorner((c) => CORNERS[(CORNERS.indexOf(c) + 1) % CORNERS.length] ?? 'br');
  return (
    <button
      type="button"
      className="mx-self"
      data-corner={corner}
      onClick={next}
      title="You — click to move"
      aria-label="Your camera. Click to move to another corner."
    >
      <TileFor trackRef={trackRef} mirror overlay={overlay} />
    </button>
  );
}

/**
 * The floating dock: the controls a person reaches for during a call, and
 * nothing they would reach for once a meeting.
 */
function Dock({
  visible,
  extra,
  panels,
  onEnter,
  onLeave,
}: {
  visible: boolean;
  extra?: ReactNode;
  panels?: ReactNode;
  onEnter?: () => void;
  onLeave?: () => void;
}) {
  return (
    <div
      className="mx-dock"
      data-visible={visible ? 'true' : 'false'}
      role="toolbar"
      aria-label="Call controls"
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
    >
      <div className="mx-dock-group">
        <TrackToggle source={Track.Source.Microphone} className="mx-dock-btn" aria-label="Microphone" />
        <MediaDeviceMenu kind="audioinput" className="mx-dock-menu" aria-label="Choose microphone">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 10l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </MediaDeviceMenu>
      </div>
      <div className="mx-dock-group">
        <TrackToggle source={Track.Source.Camera} className="mx-dock-btn" aria-label="Camera" />
        <MediaDeviceMenu kind="videoinput" className="mx-dock-menu" aria-label="Choose camera">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 10l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </MediaDeviceMenu>
      </div>
      <TrackToggle
        source={Track.Source.ScreenShare}
        className="mx-dock-btn"
        aria-label="Share screen"
        captureOptions={{ audio: true, selfBrowserSurface: 'include' }}
      />
      {extra}
      {panels}
      <DisconnectButton className="mx-dock-btn mx-dock-leave" aria-label="Leave call">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M3 14.5c5-4.7 13-4.7 18 0l-2.3 2.6-3.4-1.6v-2.6a12 12 0 0 0-6.6 0v2.6l-3.4 1.6z"
            fill="currentColor"
          />
        </svg>
      </DisconnectButton>
    </div>
  );
}

/**
 * Keeps the dock up while the pointer is moving or focus is inside it, and lets
 * it fade when nobody is reaching for it. A touch shows it; keyboard focus pins
 * it, so tabbing to the mute button never lands on an invisible one.
 */
function useDockVisibility() {
  const [visible, setVisible] = useState(true);
  const timer = useRef<number | undefined>(undefined);
  const pinned = useRef(false);
  // The pointer resting ON the dock — or on a menu opened from it — is a person
  // reaching for a control, not an idle screen. Without this the quality and
  // device menus faded out from under the pointer while being read.
  const hovered = useRef(false);

  const poke = useCallback(() => {
    setVisible(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (!pinned.current && !hovered.current) setVisible(false);
    }, DOCK_IDLE_MS);
  }, []);

  useEffect(() => {
    poke();
    return () => window.clearTimeout(timer.current);
  }, [poke]);

  // Pinned by KEYBOARD focus only. A mouse click on mute also focuses the
  // button, and pinning on that kept the dock up for the rest of the call.
  const onFocus = useCallback((e: FocusEvent) => {
    if (!(e.target instanceof Element)) return;
    // :focus-visible is unknown to Safari before 15.4 and matches() THROWS
    // on it there; on those, any focus pins, which is the old behaviour.
    let keyboard = true;
    try {
      keyboard = e.target.matches(':focus-visible');
    } catch {
      /* old Safari */
    }
    if (!keyboard) return;
    pinned.current = true;
    setVisible(true);
  }, []);
  const onBlur = useCallback(() => {
    pinned.current = false;
    poke();
  }, [poke]);

  const onEnter = useCallback(() => {
    hovered.current = true;
    setVisible(true);
  }, []);
  const onLeave = useCallback(() => {
    hovered.current = false;
    poke();
  }, [poke]);

  return { visible, poke, onFocus, onBlur, onEnter, onLeave };
}

export interface CallStageProps {
  /** Extra dock buttons, e.g. MN-11's quality menu. */
  dockExtra?: ReactNode;
  /** Panel toggles (transcript, chat), owned by the caller. */
  dockPanels?: ReactNode;
  /** Per-tile overlay, e.g. MN-11's stats readout. */
  tileOverlay?: TileOverlay;
}

/**
 * Layouts, by what is happening:
 *
 *   alone          — your own picture fills the stage, with a quiet "waiting"
 *   one other      — they fill the stage, you float in a corner
 *   several        — they share a grid (LiveKit's, which paginates), you float
 *   a screen share — the screen fills the stage, people run down the side
 */
export function CallStage({ dockExtra, dockPanels, tileOverlay }: CallStageProps) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  const dock = useDockVisibility();

  // An agent is not somebody in the call: the momentary participant that
  // posts an operator's `meetnixctl call ROOM say` line is one (MWE-1), and
  // it must not flash a tile onto the stage for the second it is here.
  const people = tracks.filter((t) => !t.participant.isAgent);
  const screens = people.filter((t) => t.source === Track.Source.ScreenShare);
  const cameras = people.filter((t) => t.source === Track.Source.Camera);
  const self = cameras.find((t) => t.participant.isLocal);
  const others = cameras.filter((t) => !t.participant.isLocal);

  let body: ReactNode;
  let layout: string;
  const [main, ...restScreens] = screens;
  const [only] = others;
  if (main) {
    layout = 'focus';
    const side = [...restScreens, ...others];
    body = (
      <>
        <div className="mx-focus-main">
          <TileFor trackRef={main} fit="contain" overlay={tileOverlay} />
        </div>
        {side.length > 0 && (
          <CarouselLayout tracks={side} orientation="vertical" className="mx-focus-side">
            <Tile overlay={tileOverlay} />
          </CarouselLayout>
        )}
      </>
    );
  } else if (others.length === 0) {
    layout = 'alone';
    body = (
      <>
        {self && <TileFor trackRef={self} fit="contain" mirror overlay={tileOverlay} />}
        <div className="mx-waiting" role="status">
          <span className="mx-waiting-dot" aria-hidden="true" />
          Waiting for others to join
        </div>
      </>
    );
  } else if (others.length === 1 && only) {
    layout = 'duo';
    body = <TileFor trackRef={only} fit="contain" overlay={tileOverlay} />;
  } else {
    layout = 'grid';
    body = (
      <GridLayout tracks={others} className="mx-grid">
        <Tile overlay={tileOverlay} />
      </GridLayout>
    );
  }

  return (
    <div
      className="mx-stage2"
      data-layout={layout}
      onPointerMove={dock.poke}
      onPointerDown={dock.poke}
      onFocusCapture={dock.onFocus}
      onBlurCapture={dock.onBlur}
    >
      <div className="mx-stage2-body">{body}</div>
      {layout !== 'alone' && self && <SelfView trackRef={self} overlay={tileOverlay} />}
      {/* THE AUDIO UNLOCK (MN-29). A browser may refuse to play sound on a page
          nobody has touched yet, and the call page always starts as one: a
          guest arrives by a form POST, which is a fresh document with no user
          gesture. iOS Safari refuses; the call then looks fine and is silent.
          LiveKit's prebuilt control bar carried this button and MN-14 dropped
          it with the prebuilt. It renders nothing while audio can play, so
          most people never see it; when it shows, one tap starts the sound. */}
      <StartAudio label="Tap to hear the call" className="mx-start-audio" />
      <Dock
        visible={dock.visible}
        extra={dockExtra}
        panels={dockPanels}
        onEnter={dock.onEnter}
        onLeave={dock.onLeave}
      />
    </div>
  );
}
